import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageShell from '../Components/ui/PageShell';
import DataTable from '../Components/ui/DataTable';
import AppModal from '../Components/ui/AppModal';
import { getDecryptedData, getUserType } from '../utils/authStorage';
import { getUpcomingInterviews, scheduleInterview } from '../services/workflowService';
import { showSuccess, showError } from '../utils/toast';
import { getApiErrorMessage } from '../utils/apiError';

function InterviewsSchedule() {
  const userId = getDecryptedData('Id');
  const userType = getUserType();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [days, setDays] = useState(7);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [saving, setSaving] = useState(false);

  const loadInterviews = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadError('');
    try {
      const response = await getUpcomingInterviews(userId, days);
      setData(response);
    } catch (error) {
      console.error('Interviews load failed:', error);
      setLoadError(error.message || 'Failed to load interview schedule.');
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  useEffect(() => {
    loadInterviews();
  }, [loadInterviews]);

  const interviews = data?.interviews || [];

  const columns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'recruiter', label: 'Recruiter', sortable: true },
    { key: 'interviewDateLabel', label: 'Date & Time', sortable: true },
    {
      key: 'actions',
      label: 'Action',
      render: (row) => (
        (userType === 'Admin' || userType === 'TeamLead') ? (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              setScheduleTarget(row);
              setScheduleDate(row.interviewDate ? row.interviewDate.slice(0, 10) : '');
              setScheduleTime(row.interviewTime || '');
            }}
          >
            Schedule
          </button>
        ) : '—'
      ),
    },
  ], [userType]);

  const handleScheduleSave = async () => {
    if (!scheduleTarget?.candidateId || !scheduleDate) return;
    setSaving(true);
    try {
      await scheduleInterview(scheduleTarget.candidateId, {
        interviewDate: scheduleDate,
        interviewTime: scheduleTime,
      });
      showSuccess('Interview scheduled successfully ✓');
      setScheduleTarget(null);
      await loadInterviews();
    } catch (error) {
      console.error('Schedule interview failed:', error);
      showError(getApiErrorMessage(error, 'Failed to schedule interview.'));
    } finally {
      setSaving(false);
    }
  };

  const subtitle = userType === 'Admin'
    ? 'Upcoming interviews across all requirements.'
    : userType === 'TeamLead'
      ? 'Upcoming interviews for your team and assigned clients.'
      : 'Your upcoming interview schedule.';

  return (
    <PageShell
      title="Interview Schedule"
      subtitle={subtitle}
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Interviews' },
      ]}
      backTo="/dashboard"
    >
      {loadError && <div className="alert alert-danger">{loadError}</div>}

      <div className="hr-section-toolbar">
        <h3 className="hr-section-title" style={{ margin: 0 }}>Upcoming Interviews</h3>
        <select
          className="form-select hr-filter-select"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          aria-label="Interview window"
        >
          <option value={1}>Next 1 day</option>
          <option value={3}>Next 3 days</option>
          <option value={7}>Next 7 days</option>
          <option value={14}>Next 14 days</option>
        </select>
      </div>

      <div className="hr-card" style={{ padding: '16px' }}>
        {loading ? (
          <p className="hr-empty-state">Loading interview schedule...</p>
        ) : (
          <DataTable
            columns={columns}
            data={interviews}
            searchPlaceholder="Search candidate, client, or req id"
            emptyMessage="No upcoming interviews in this window."
            exportFileName="interview-schedule.csv"
          />
        )}
      </div>

      <AppModal
        show={!!scheduleTarget}
        onHide={() => setScheduleTarget(null)}
        title="Schedule Interview"
        size="md"
      >
        {scheduleTarget && (
          <div className="hr-schedule-form">
            <p><strong>{scheduleTarget.candidateName}</strong> — {scheduleTarget.client}</p>
            <div className="mb-3">
              <label className="form-label" htmlFor="interview-date">Interview Date</label>
              <input
                id="interview-date"
                type="date"
                className="form-control"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="interview-time">Interview Time</label>
              <input
                id="interview-time"
                type="time"
                className="form-control"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
              />
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-secondary" onClick={() => setScheduleTarget(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary hr-btn-primary"
                onClick={handleScheduleSave}
                disabled={saving || !scheduleDate}
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        )}
      </AppModal>
    </PageShell>
  );
}

export default InterviewsSchedule;
